import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ROW_DRAWER_FIELD_LABEL_KEY,
    ROW_DRAWER_UNIT_LABEL_KEY,
    ROW_DRAWER_VAT_LABEL_KEY,
} from './RowDrawerFields';
import type {
    RowDrawerGroupMismatch,
    RowDrawerGroupValue,
} from './rowDrawerResolutions';

type RowDrawerGroupValuesProps = {
    sku: string;
    mismatch: RowDrawerGroupMismatch;
    onTake: (rowNumber: number) => void;
};

const isKeyOf = <Map extends object>(
    map: Map,
    key: string,
): key is Extract<keyof Map, string> => Object.hasOwn(map, key);

/** A value as the form's select would name it; the file's own text otherwise. */
const valueText = (t: TFunction, { field, value }: RowDrawerGroupValue) => {
    if (value === '') {
        return t('catalogImport.products.drawer.group.blank');
    }
    if (field === 'unit' && isKeyOf(ROW_DRAWER_UNIT_LABEL_KEY, value)) {
        return t(ROW_DRAWER_UNIT_LABEL_KEY[value]);
    }
    if (field === 'vatType' && isKeyOf(ROW_DRAWER_VAT_LABEL_KEY, value)) {
        return t(ROW_DRAWER_VAT_LABEL_KEY[value]);
    }
    return value;
};

/**
 * A group mismatch's rows inside the problem card: one sentence on what the
 * buttons do, then a card per row of the group, in row order, with its
 * values for the fields that differ ("Name", "Category" …, as the form
 * labels them; blank says so). The row the bulk fix would take (the first
 * that fills them in recognisably) is marked **Suggested**, and a line under the cards says why. Every other
 * row has **Use for all rows**, described by its row's title so a screen
 * reader hears which; the drawer's own row is marked and has none.
 */
export const RowDrawerGroupValues: React.FC<RowDrawerGroupValuesProps> = ({
    sku,
    mismatch,
    onTake,
}) => {
    const { t } = useTranslation('pages');
    const idPrefix = React.useId();

    return (
        <div
            className="mt-2 flex w-full flex-col gap-2"
            {...getNodeTestIdProp('catalog-import-row-drawer-group')}
        >
            <Typography type="body-sm">
                {t('catalogImport.products.drawer.group.intro', { sku })}
            </Typography>
            <ul className="flex flex-col gap-2">
                {mismatch.rows.map((row) => {
                    const titleId = `${idPrefix}-row-${row.rowNumber}`;
                    return (
                        <li
                            key={row.rowNumber}
                            {...getNodeTestIdProp(
                                `catalog-import-row-drawer-group-row-${row.rowNumber}`,
                            )}
                        >
                            <Card
                                className="w-full gap-2 p-3"
                                variant="secondary"
                            >
                                <Card.Header className="flex-row flex-wrap items-center justify-between gap-2">
                                    <span className="flex flex-wrap items-center gap-2">
                                        <Card.Title
                                            id={titleId}
                                            render={(props) => (
                                                <span {...props} />
                                            )}
                                        >
                                            {t(
                                                row.isCurrent
                                                    ? 'catalogImport.products.drawer.group.currentRow'
                                                    : 'catalogImport.products.drawer.group.row',
                                                { row: row.rowNumber },
                                            )}
                                        </Card.Title>
                                        {row.rowNumber ===
                                        mismatch.suggestedRow ? (
                                            <Chip
                                                color="accent"
                                                size="sm"
                                                variant="soft"
                                                {...getNodeTestIdProp(
                                                    'catalog-import-row-drawer-group-suggested',
                                                )}
                                            >
                                                {t(
                                                    'catalogImport.products.drawer.fix.suggested',
                                                )}
                                            </Chip>
                                        ) : null}
                                    </span>
                                    {row.isCurrent ? null : (
                                        <Button
                                            aria-describedby={titleId}
                                            onPress={() =>
                                                onTake(row.rowNumber)
                                            }
                                            size="sm"
                                            variant="outline"
                                            {...getNodeTestIdProp(
                                                `catalog-import-row-drawer-group-take-${row.rowNumber}`,
                                            )}
                                        >
                                            {t(
                                                'catalogImport.products.drawer.group.take',
                                            )}
                                        </Button>
                                    )}
                                </Card.Header>
                                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
                                    {row.values.map((value) => (
                                        <React.Fragment key={value.field}>
                                            <dt className="text-muted">
                                                {t(
                                                    ROW_DRAWER_FIELD_LABEL_KEY[
                                                        value.field
                                                    ],
                                                )}
                                            </dt>
                                            <dd className="break-words">
                                                {valueText(t, value)}
                                            </dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            </Card>
                        </li>
                    );
                })}
            </ul>
            {mismatch.suggestedRow === null ? null : (
                <Typography color="muted" type="body-xs">
                    {t(
                        'catalogImport.products.drawer.fix.groupFields.suggestedWhy',
                    )}
                </Typography>
            )}
        </div>
    );
};
